import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchDefinitionOnlyFamilySnapshot } from '../../../shared/lib/revit/definitionOnlyFamilySnapshot';
import { fetchAvailableProjectParameters } from '../../../shared/lib/hooks/useParameterAddition';
import { toBoltConfigurationInput } from '../../../shared/lib/utils/boltConfigurationInput';
import {
  areBoltParametersAlreadyAvailable,
  buildBoltFamilySnapshotRequest,
  findFamiliesWithObjectType,
  type BoltModelingType,
} from '../../../shared/services/boltParameterSetup';
import type {
  BoltConfiguration,
  ConnectorParameterBindingKind,
  ConnectorParameterDestination,
} from '../../../shared/types';
import {
  AVAILABILITY_RECHECK_DEBOUNCE_MS,
  useWorkflowAvailabilityProgress,
} from './useWorkflowAvailabilityProgress';

interface UseBoltFamilyAvailabilityInput {
  activeProjectKey: string | null;
  config: BoltConfiguration;
  objectTypeParameterName: string;
  objectTypeDestination: ConnectorParameterDestination;
  objectTypeBindingKind: ConnectorParameterBindingKind;
  targetObjectType: BoltModelingType;
}

export function useBoltFamilyAvailability({
  activeProjectKey,
  config,
  objectTypeParameterName,
  objectTypeDestination,
  objectTypeBindingKind,
  targetObjectType,
}: UseBoltFamilyAvailabilityInput) {
  const [autoConfirmed, setAutoConfirmed] = useState(false);
  const [autoConfirmedProjectKey, setAutoConfirmedProjectKey] = useState<string | null>(null);
  const autoConfirmedRef = useRef(autoConfirmed);
  const activeProjectKeyRef = useRef(activeProjectKey);
  const structuralKeyRef = useRef<string | null>(null);
  const checkInFlightRef = useRef(false);
  const configurationInput = useMemo(
    () => toBoltConfigurationInput(config, {
      parameterName: objectTypeParameterName,
      bindingKind: objectTypeBindingKind,
    }),
    [config, objectTypeBindingKind, objectTypeParameterName]
  );
  const {
    availabilityProgress,
    beginAvailabilityCheck,
    endAvailabilityCheck,
    isCheckingAvailability,
    updateAvailabilityProgressFromHost,
  } = useWorkflowAvailabilityProgress({
    title: 'Checking bolt parameters in Revit',
    message: `Revit is checking ${targetObjectType} families and parameter bindings.`,
    detail: 'This check detects existing setup. Continue loads the reference family and creates or repairs missing bindings.',
    completeMessage: 'Bolt parameter check complete',
    errorMessage: 'Revit could not check bolt parameters. Keep the intended model active and try again.',
  });

  useEffect(() => { autoConfirmedRef.current = autoConfirmed; }, [autoConfirmed]);
  useEffect(() => { activeProjectKeyRef.current = activeProjectKey; }, [activeProjectKey]);

  useEffect(() => {
    let isActive = true;
    let checkId: number | null = null;
    const availabilityProjectKey = activeProjectKey;
    const structuralKey = [
      availabilityProjectKey ?? '', targetObjectType, config.parameterDestination,
      config.parameterBindingKind, objectTypeParameterName, objectTypeDestination,
      objectTypeBindingKind,
    ].join('|');
    const isStructuralChange = structuralKeyRef.current !== structuralKey;
    structuralKeyRef.current = structuralKey;
    if (autoConfirmedRef.current) {
      autoConfirmedRef.current = false;
      setAutoConfirmed(false);
      setAutoConfirmedProjectKey(null);
    }

    const checkAvailability = async (silent: boolean) => {
      if (checkInFlightRef.current) return;
      checkInFlightRef.current = true;
      const currentCheckId = beginAvailabilityCheck({ silent });
      checkId = currentCheckId;
      try {
        const snapshotRequest = buildBoltFamilySnapshotRequest(
          configurationInput,
          objectTypeDestination
        );
        // Availability must judge family documents, not swept element rows:
        // the broad snapshot reads project-bound parameters as present on
        // families that never received them, which would auto-confirm setup
        // that only exists at project/type level in the wrong mode.
        const snapshot = await fetchDefinitionOnlyFamilySnapshot({
          parameterNames: snapshotRequest.parameterNames,
          requestedParameters: snapshotRequest.requestedParameters,
          onProgress: silent
            ? undefined
            : (hostProgress) => updateAvailabilityProgressFromHost(
                currentCheckId,
                hostProgress
              ),
        });
        // An unavailable snapshot (runtime down, stale connector DLL) yields
        // no families, so the availability verdict stays negative instead of
        // auto-confirming — same failure semantics as the broad snapshot.
        const families = snapshot.families;
        const availableProjectParameters =
          configurationInput.parameterDestination === 'project' &&
          findFamiliesWithObjectType(
            families,
            targetObjectType,
            objectTypeParameterName
          ).length > 0
            ? await fetchAvailableProjectParameters({
                cacheScopeKey: availabilityProjectKey,
                onProgress: silent
                  ? undefined
                  : (hostProgress) => updateAvailabilityProgressFromHost(
                      currentCheckId,
                      hostProgress
                    ),
              })
            : [];
        const isAvailable = areBoltParametersAlreadyAvailable(
          configurationInput,
          families,
          availableProjectParameters
        );
        if (
          isActive && activeProjectKeyRef.current === availabilityProjectKey &&
          autoConfirmedRef.current !== Boolean(isAvailable)
        ) {
          setAutoConfirmed(Boolean(isAvailable));
          setAutoConfirmedProjectKey(isAvailable ? availabilityProjectKey : null);
        }
      } finally {
        checkInFlightRef.current = false;
        endAvailabilityCheck(currentCheckId);
      }
    };

    if (isStructuralChange) {
      void checkAvailability(false).catch(() => undefined);
      return () => {
        isActive = false;
        if (checkId !== null) endAvailabilityCheck(checkId);
      };
    }
    const timer = globalThis.setTimeout(
      () => void checkAvailability(true).catch(() => undefined),
      AVAILABILITY_RECHECK_DEBOUNCE_MS
    );
    return () => {
      isActive = false;
      globalThis.clearTimeout(timer);
      if (checkId !== null) endAvailabilityCheck(checkId);
    };
  }, [
    activeProjectKey, config.parameterBindingKind, config.parameterDestination,
    configurationInput, objectTypeBindingKind, objectTypeDestination,
    objectTypeParameterName, beginAvailabilityCheck,
    endAvailabilityCheck, updateAvailabilityProgressFromHost,
    targetObjectType,
  ]);

  return {
    autoDefaultPropertiesConfirmedForActiveProject:
      autoConfirmed && autoConfirmedProjectKey === activeProjectKey,
    isCheckingAvailability,
    availabilityProgress,
  };
}
